import { memo } from 'react';
import { LABEL_STYLE } from '../shared/constants';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, LabelList,
} from 'recharts';
import { ChartWrapper } from '../shared/ChartWrapper';
import { NoChartData } from '../shared/NoChartData';
import { useProgressData } from './useProgressData';

const TAG_COLORS = {
  'Not Provided': '#cbd5e1',
  'Not Targeted': '#94a3b8',
  'Significant': '#3B9B61',
  'Principal': '#006837',
};

export const ImpactAreaChart = memo(function ImpactAreaChart() {
  const { byImpactAreaAndFunding, filteredResults } = useProgressData();

  if (!byImpactAreaAndFunding) return (
    <ChartWrapper title="Impact Area Tags">
      <NoChartData message="No impact area data available" detail="This chart shows how results are tagged across CGIAR impact areas (Gender, Climate, Nutrition, Biodiversity, Poverty) by funding type. No impact area tag data is available for the current selection." />
    </ChartWrapper>
  );

  const impactAreas = ['gender', 'climate', 'nutrition', 'biodiversity', 'poverty'];
  const labels = ['Gender', 'Climate', 'Nutrition', 'Biodiversity', 'Poverty'];

  const chartData = impactAreas.map((area, idx) => {
    const w = byImpactAreaAndFunding[area];
    return {
      name: labels[idx],
      'Not Targeted': (w?.W1W2?.[0] || 0) + (w?.W3?.[0] || 0),
      'Significant': (w?.W1W2?.[1] || 0) + (w?.W3?.[1] || 0),
      'Principal': (w?.W1W2?.[2] || 0) + (w?.W3?.[2] || 0),
      'Not Provided': (w?.W1W2?.[-1] || 0) + (w?.W3?.[-1] || 0),
    };
  });

  const stackKeys = ['Not Provided', 'Not Targeted', 'Significant', 'Principal'];

  const renderStackTotal = (props) => {
    const { x, y, width, index } = props;
    const entry = chartData[index];
    if (!entry) return null;
    const total = stackKeys.reduce((sum, key) => sum + (entry[key] || 0), 0);
    if (total === 0) return null;
    return (
      <text x={x + width / 2} y={y - 4} fill={LABEL_STYLE.fill} fontSize={LABEL_STYLE.fontSize} textAnchor="middle">
        {total}
      </text>
    );
  };

  return (
    <ChartWrapper title="Impact Area Targeting" info="How results are tagged across the five CGIAR Impact Areas (Gender, Climate, Nutrition, Biodiversity, Poverty) at Principal, Significant, or Not Targeted levels." chartData={chartData} exportFilename="impact-area-targeting" sourceResults={filteredResults}>
      <ResponsiveContainer width="100%" height={300}>
        <BarChart data={chartData}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="name" tick={{ fontSize: 11 }} />
          <YAxis tick={{ fontSize: 12 }} />
          <Tooltip content={({ active, payload, label }) => {
            if (!active || !payload?.length) return null;
            const lookup = {};
            payload.forEach(p => { lookup[p.dataKey] = p; });
            const total = stackKeys.reduce((sum, key) => sum + (lookup[key]?.value || 0), 0);
            return (
              <div style={{ background: '#fff', padding: '8px 12px', borderRadius: '8px', border: '1px solid #e5e7eb', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', fontSize: '13px', lineHeight: 1.6 }}>
                <div style={{ fontWeight: 600, color: '#1e293b', marginBottom: 2 }}>{label}</div>
                {['Principal', 'Significant', 'Not Targeted', 'Not Provided'].map(key => lookup[key] && (
                  <div key={key} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span style={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: TAG_COLORS[key], display: 'inline-block', flexShrink: 0 }} />
                    <span style={{ color: '#4b5563' }}>{key}: <strong>{lookup[key].value?.toLocaleString()}</strong></span>
                  </div>
                ))}
                <div style={{ marginTop: 4, paddingTop: 4, borderTop: '1px solid #e5e7eb', color: '#6b7280', fontSize: 12 }}>Total: {total.toLocaleString()}</div>
              </div>
            );
          }} />
          <Legend content={() => (
            <div style={{ display: 'flex', justifyContent: 'center', gap: 16, marginTop: 4 }}>
              {[
                { label: 'Not Provided', color: TAG_COLORS['Not Provided'] },
                { label: 'Not Targeted', color: TAG_COLORS['Not Targeted'] },
                { label: 'Significant', color: TAG_COLORS['Significant'] },
                { label: 'Principal', color: TAG_COLORS['Principal'] },
              ].map(({ label, color }) => (
                <span key={label} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 12, color: '#374151' }}>
                  <span style={{ width: 10, height: 10, background: color, borderRadius: 2, display: 'inline-block' }} />
                  {label}
                </span>
              ))}
            </div>
          )} />
          <Bar dataKey="Not Provided" stackId="a" fill={TAG_COLORS['Not Provided']} />
          <Bar dataKey="Not Targeted" stackId="a" fill={TAG_COLORS['Not Targeted']} />
          <Bar dataKey="Significant" stackId="a" fill={TAG_COLORS['Significant']} />
          <Bar dataKey="Principal" stackId="a" fill={TAG_COLORS['Principal']} radius={[4, 4, 0, 0]}>
            <LabelList content={renderStackTotal} />
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </ChartWrapper>
  );
});
